import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { T } from "react-translator-component";
import axios, { CancelTokenSource } from "axios";
import appServices from "../../../services/app-services/appServices";
import { displayUserNickname } from "../../../util/utils";
import {
  Modal,
  Box,
  Typography,
  TextField,
  Slide,
  InputAdornment,
  CircularProgress
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { colors } from "../../../common/colors";

interface AddChatUserListProps {
  open: boolean;
  onClose: () => void;
  onSelect: (options: any) => void;
}

const FindUserModal: React.FC<AddChatUserListProps> = ({
  open,
  onClose,
  onSelect,
}) => {
  const groupInfo = useSelector((state: any) => state.gcGroup);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [userDataList, setUserDataList] = useState<any[]>();
  const [loading, setLoading] = useState<boolean>(false);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);

  useEffect(() => {
    searchUsersOnline();
  }, [searchTerm]);

  const searchUsersOnline = async () => {
    if (searchTerm.trim() === "") {
      setUserDataList([]);
      return;
    }

    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Operation canceled due to new request.");
    }
    cancelTokenRef.current = axios.CancelToken.source();

    try {
      setLoading(true);
      const res = await appServices.searchUsers(searchTerm, null, cancelTokenRef.current.token)
      let userList = []
      if (groupInfo.piiRequired === "YES") {
        userList = res.users.filter((user: any) => user?.name)
      } else {
        userList = res.users
      }
      setUserDataList(userList);
    } catch (error) {
      if (axios.isCancel(error)) {
        // pass
      } else {
        console.error("Error fetching search date:", error);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleOptionSelect = async (user: any) => {
    try {
      onSelect(user);
      onClose();
    } catch (error) {
      console.error("Failed to add user to chat:", error);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      slotProps={{
        backdrop: { style: modalBackdropStyle }
      }}
    >
      <Slide direction="up" in={open} mountOnEnter unmountOnExit>
        <Box sx={containerStyle}>
          <Box sx={headerStyle}>
            <Box onClick={onClose} sx={backButtonStyle}>
              <Typography>{T("back_kanji")}</Typography>
              <KeyboardArrowDownIcon />
            </Box>
            <Typography>{T("add_chat_user_list_title")}</Typography>
          </Box>

          <Box sx={searchBoxStyle}>
            <Typography sx={instructionStyle}>{T("can_search_for_users")}</Typography>
            <TextField
              sx={textFieldStyle}
              id="outlined-basic"
              variant="outlined"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                endAdornment:
                  <InputAdornment position="end">
                    {loading && <CircularProgress size={20} />}
                  </InputAdornment>,
              }}
            />
          </Box>

          <Box sx={listContainerStyle}>
            {userDataList?.map((userData: any) => {
              return (
                <Box sx={optionStyle} key={userData.id}>
                  <Box sx={optionContentStyle}>
                    <Typography style={{ flex: 1 }}>
                      {displayUserNickname(userData?.nickname)}
                    </Typography>
                    <AddCircleOutlineIcon
                      sx={iconStyle(colors.farm_blue200)}
                      onClick={() => handleOptionSelect(userData)}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
};

const modalBackdropStyle = { backgroundColor: "transparent" };
const containerStyle = {
  position: "absolute" as "absolute",
  top: 0,
  left: 0,
  transform: "translate(-50%, -50%)",
  width: "100%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: "20px 0",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  padding: "20px",
};
const headerStyle = { textAlign: "center", marginBottom: "10px" };
const backButtonStyle = { display: "flex", justifyContent: "center", alignItems: "center", cursor: "pointer" };
const searchBoxStyle = { padding: "10px 0", width: "100%", marginTop: "10px" };
const instructionStyle = { mb: 1 };
const textFieldStyle = { width: "100%" };
const listContainerStyle = { marginBottom: "20px" };
const optionStyle = { width: "100%", borderBottom: "1px solid", borderColor: colors.farm_gray100 };
const optionContentStyle = { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 10px 0px 10px" };
const iconStyle = (color: string) => ({ color, fontSize: "34px", cursor: "pointer" });

export default FindUserModal;
